import * as Yup from 'yup';
import axios from 'axios';

export const schema = intl =>
  Yup.object().shape({
    positioning: Yup.object().shape({
      z: Yup.number().required(
        intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
      ),
    }),
    statusInterval: Yup.number()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
      .min(
        0,
        intl.formatMessage({ id: 'AUTH.VALIDATION.MIN_VALUE_FIELD' }) + ' 0'
      ),
    networkId: Yup.number().required(
      intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
    ),
    encryptionKey: Yup.string()
      .matches(
        /^[0-9a-fA-F]+$/,
        intl.formatMessage({ id: 'INVALID_HEXADECIMAL' })
      )
      .test(
        'hexadecimal',
        intl.formatMessage({ id: 'INVALID_HEXADECIMAL' }),
        val => parseInt(val, 16) > 0 || !val
      )
      .max(
        32,
        intl.formatMessage({ id: 'AUTH.VALIDATION.MAX_LENGTH_FIELD' }) + ' 32'
      )
      .when('encryptionEnabled', {
        is: true,
        then: Yup.string()
          .required(
            intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
          )
          .min(
            1,
            intl.formatMessage({ id: 'AUTH.VALIDATION.MIN_LENGTH_FIELD' }) +
              ' 1'
          ),
      }),
  });

export const bridgeSchema = intl =>
  Yup.object().shape({
    supportThirdPartyBridges: Yup.bool().required(
      intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
    ),
    statusInterval: Yup.number()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
      .min(
        0,
        intl.formatMessage({ id: 'AUTH.VALIDATION.MIN_VALUE_FIELD' }) + ' 0'
      ),
    txPeriodMs: Yup.number()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
      .min(
        0,
        intl.formatMessage({ id: 'AUTH.VALIDATION.MIN_LENGTH_FIELD' }) + ' 0'
      ),
    rxTxPeriodMs: Yup.number()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
      .min(
        0,
        intl.formatMessage({ id: 'AUTH.VALIDATION.MIN_LENGTH_FIELD' }) + ' 0'
      ),
    energyPattern: Yup.number()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
      .min(
        0,
        intl.formatMessage({ id: 'AUTH.VALIDATION.MIN_LENGTH_FIELD' }) + ' 0'
      ),
    pacerInterval: Yup.number()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
      .min(
        0,
        intl.formatMessage({ id: 'AUTH.VALIDATION.MIN_LENGTH_FIELD' }) + ' 0'
      ),
    sub1GhzFrequency: Yup.number()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
      .min(
        0,
        intl.formatMessage({ id: 'AUTH.VALIDATION.MIN_LENGTH_FIELD' }) + ' 0'
      ),
    globalPacingGroup: Yup.number()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
      .min(
        0,
        intl.formatMessage({ id: 'AUTH.VALIDATION.MIN_LENGTH_FIELD' }) + ' 0'
      ),
  });

export const geoAnchorSchema = intl =>
  Yup.object().shape({
    lat: Yup.number()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
      .min(-90, intl.formatMessage({ id: 'VALIDATION.LATITUDE' }))
      .max(90, intl.formatMessage({ id: 'VALIDATION.LATITUDE' })),
    long: Yup.number()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
      .min(-180, intl.formatMessage({ id: 'VALIDATION.LONGITUDE' }))
      .max(180, intl.formatMessage({ id: 'VALIDATION.LONGITUDE' })),
  });

export const saveShadow = (
  callback,
  oldValues,
  mark,
  floorPlanId,
  pos,
  setPendencies
) => async (values, { setSubmitting }) => {
  setSubmitting(true);
  try {
    if (values.isOrigin) {
      values.positioning.y = 0;
      values.positioning.x = 0;
    } else {
      values.positioning.y = +pos.lat.toFixed(3);
      values.positioning.x = +pos.lng.toFixed(3);
    }
    delete values.useEthernet;
    delete values.label;
    const data = {
      oldValues,
      newValues: {
        ...values,
        networkId: +values.networkId,
        isOrigin: undefined,
      },
    };
    const { lat, lng } = mark.getLatLng();
    await Promise.all([
      axios.put(`/thing-shadows/${mark.thing.thingName}`, data),
      axios.put(`/things/${mark.thing.thingName}`, {
        lat,
        lng,
        floorPlanId,
        leafletCoordinatesInMeters: {
          x: values.positioning.x,
          y: values.positioning.y,
        },
      }),
    ]);
    if (oldValues.isInitiator && !values.isInitiator) {
      setPendencies(state => ({
        ...state,
        initiator: true,
      }));
    } else if (!oldValues.isInitiator && values.isInitiator) {
      setPendencies(state => ({
        ...state,
        initiator: false,
      }));
    }
    if (oldValues.isBridge && !values.isBridge) {
      setPendencies(state => ({
        ...state,
        bridge: true,
      }));
    } else if (!oldValues.isBridge && values.isBridge) {
      setPendencies(state => ({
        ...state,
        bridge: false,
      }));
    }
    callback(values.isOrigin);
  } finally {
    setSubmitting(false);
  }
};

export const saveBridgeShadow = (
  callback,
  oldValues,
  mark,
  floorPlanId
) => async (values, { setSubmitting }) => {
  setSubmitting(true);
  try {
    const {
      statusInterval: oldStatusInterval,
      gatewayMode: oldGatewayMode,
      ...oldRest
    } = oldValues;
    const {
      statusInterval: newStatusInterval,
      gatewayMode: newGatewayMode,
      ...newRest
    } = values;
    const data = {
      oldValues: {
        classic: {
          statusInterval: oldStatusInterval,
          gatewayMode: oldGatewayMode,
        },
        ble: {
          wiliot: {
            ...oldRest,
          },
        },
      },
      newValues: {
        classic: {
          statusInterval: newStatusInterval,
          gatewayMode: newGatewayMode,
        },
        ble: {
          wiliot: {
            ...newRest,
          },
        },
      },
    };
    const { lat, lng } = mark.getLatLng();
    await Promise.all([
      axios.put(`/thing-shadows/${mark.thing.thingName}`, data),
      axios.put(`/things/${mark.thing.thingName}`, {
        lat,
        lng,
        floorPlanId,
        leafletCoordinatesInMeters: null,
      }),
    ]);
    callback();
  } finally {
    setSubmitting(false);
  }
};

export const compareDesiredAndReported = (desired, reported) => key =>
  desired[key] === reported[key];
